import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, ExternalLink, Copy, CircleCheck as CheckCircle, Music2, Users, Trophy, Eye } from 'lucide-react';
import { KioskService } from '../services/KioskService';

interface LeagueRow {
  id: string;
  slug: string;
  name: string;
  status: string;
  primary_color: string;
  passcode: string | null;
  created_at: string;
}

export const AdminSingAlongLeagues: React.FC = () => {
  const [leagues, setLeagues] = useState<LeagueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<LeagueRow | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await KioskService.adminListLeagues();
      setLeagues((data as LeagueRow[]).filter(l => (l as any).signup_mode === 'passcode'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await KioskService.adminDeleteLeague(confirmDelete.id);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const copyUrl = async (slug: string) => {
    const url = `${window.location.origin}/league/${slug}`;
    await navigator.clipboard.writeText(url);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 1500);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Music2 className="w-7 h-7 text-orange-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Sing-Along Leagues</h1>
            <p className="text-sm text-slate-400">Family and friends sing-along events (passcode-based)</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-center py-12">Loading...</div>
      ) : leagues.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-700 rounded-xl text-slate-400">
          <Music2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No sing-along leagues yet.</p>
          <p className="text-xs mt-1 text-slate-500">Sing-along leagues are created by users from the Sing-Along setup flow.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {leagues.map((league) => (
            <div
              key={league.id}
              className="bg-slate-800/60 border border-slate-700 rounded-xl p-5"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: league.primary_color || '#f97316' }}
                  >
                    {league.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{league.name}</h3>
                    <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                      <span className="font-mono">/league/{league.slug}</span>
                      <StatusBadge status={league.status} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {league.passcode && (
                    <span className="px-2.5 py-1 bg-orange-500/10 border border-orange-500/30 rounded-lg text-orange-300 text-xs font-mono tracking-wider">
                      {league.passcode}
                    </span>
                  )}
                  <button
                    onClick={() => copyUrl(league.slug)}
                    className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg transition"
                    title="Copy invite URL"
                  >
                    {copiedSlug === league.slug ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <a
                    href={`/league/${league.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg transition"
                    title="Open join page"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <a
                    href={`/sing-along/${league.slug}/leaderboard`}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg transition"
                    title="View leaderboard"
                  >
                    <Trophy className="w-4 h-4" />
                  </a>
                  <Link
                    to={`/admin/kiosk-leagues/${league.id}?tab=participants`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
                  >
                    <Users className="w-3.5 h-3.5" /> Participants
                  </Link>
                  <Link
                    to={`/admin/kiosk-leagues/${league.id}?tab=songs`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
                  >
                    <Eye className="w-3.5 h-3.5" /> Songs
                  </Link>
                  <button
                    onClick={() => setConfirmDelete(league)}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Delete Sing-Along</h2>
              <button onClick={() => setConfirmDelete(null)} className="text-slate-400 hover:text-white">&times;</button>
            </div>
            <p className="text-slate-300 mb-4">
              Delete "{confirmDelete.name}"? This will remove all participants and recordings. This cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-slate-300">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    draft: 'bg-slate-600 text-slate-200',
    open: 'bg-green-500/20 text-green-300 border border-green-500/30',
    closed: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || map.draft}`}>
      {status}
    </span>
  );
};
